import { FundingOption } from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import useBelieveAccount from 'features/believe/application/hooks/useBelieveAccount';
import useWalletAvailability from 'features/believe/application/hooks/useWalletAvailability';
import {
  getFlowNameForAnalytics,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import { useFundingCallback } from 'features/onboarding/hooks/useFundingCallback';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';

const TabapayTransactionSuccess = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'tabapayTransferSuccess',
  });
  const { setShouldRefreshTransactions } = useTabaPayAddress();
  const { refreshTiles, data, fetching, error } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.ACCOUNT_ACTION_TILE_ADD_MONEY_DDA,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_WELCOME,
  ] as string[]);

  const { invokeCallback } = useFundingCallback(FundingOption.DebitCard);

  const { amount, isPartialAuth, isBelieveDay0Flow, believeCardId } =
    useLocalSearchParams<{
      amount?: string;
      isPartialAuth?: string;
      isBelieveDay0Flow?: string;
      believeCardId?: string;
    }>();

  const isWalletProvisioningAvailable = useWalletAvailability();
  const { account: believeAccount } = useBelieveAccount();
  const isBelieveDay0 = isBelieveDay0Flow === 'true';

  useEffect(() => {
    if ((data?.tiles || error) && !fetching) {
      setShouldRefreshTransactions(true);
      router.back();
    }
  }, [data, error, fetching, setShouldRefreshTransactions, router]);
  const flowName = getFlowNameForAnalytics(TabaPayEntryFlowType.DCFFlow);
  const analyticsFlowName = isBelieveDay0
    ? 'dcf_believe_day_0_application'
    : flowName;

  const dcfCompletedAnalytics = usePressableAnalytics({
    analyticsId: 'dcfCompleted',
    customAttributes: {
      amount: amount,
      flow_name: analyticsFlowName,
      subCategory: isPartialAuth === 'true' ? 'partial_auth' : undefined,
    },
  });

  useEffect(() => {
    dcfCompletedAnalytics();
  }, [dcfCompletedAnalytics]);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'tabapay-transfer-success',
    customAttributes: {
      flow_name: analyticsFlowName,
      subCategory: isPartialAuth === 'true' ? 'partial_auth' : '',
    },
  });

  const backToHome = () => {
    if (router.canGoBack()) {
      router.dismissAll();
    }
    router.back();
  };

  useBackHandler(backToHome);

  const handleNext = () => {
    if (isBelieveDay0) {
      // cardId is nullable from the application poll; AddWallet requires it.
      if (isWalletProvisioningAvailable && believeCardId) {
        router.dismissAll();
        router.replace({
          pathname: '/believe/apply/addWallet',
          params: { isBelieveDay0Flow, cardId: believeCardId },
        });
      } else if (believeAccount?.id) {
        router.dismissAll();
        router.replace(`/accounts/${believeAccount.id}`);
      } else {
        backToHome();
      }
    } else {
      refreshTiles();
      invokeCallback({ success: true });
    }
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      <SuccessScreen
        primaryButton={{
          label: translatedText('buttons.next'),
          onPress: handleNext,
          isLoading: fetching,
        }}
        eyebrowText={translatedText('eyebrowText')}
        visual="PLANE_A"
        titleText={`${formatCurrency(
          amount?.toString() ?? '0',
          true,
        )}${translatedText('title')}`}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default TabapayTransactionSuccess;
